import { JSX, KeyboardEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { BulkProviderChangeProjectTableData } from "@app/shared/provider-change-wizards/interfaces";
import { AdvancedExclusionsContext } from "@app/projects/projection-provider/advanced-exclusions/AdvancedExclusionsContext";
import BulkProjectSelectLOBTable from "@app/shared/BulkProjectSelectLOBTable";
import BulkProjectSelectTagsTable from "@app/shared/BulkProjectSelectTagsTable";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESRadioGroup } from "@app/shared/qes-standards/forms/RadioGroup";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";

interface BulkProjectionSelectProjectsProps {
  pageIsValid: (flag: boolean) => void;
}

const projectViewOptions = ["Project", "Tag", "Line of Business"] as const;
type ProjectView = typeof projectViewOptions[number];

const ProjectsTableColumnConfig: ColumnConfig = {
  details: {
    displayText: "Details",
    testId: "details",
  },
  projectId: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  projectName: {
    displayText: "Project Name",
    testId: "project-name",
  },
  rowIsDisabled: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  templateName: {
    displayText: "Template",
    testId: "template",
  },
};

const BulkProjectionSelectProjects = (props: BulkProjectionSelectProjectsProps): JSX.Element => {

  const [projectSearchString, setProjectSearchString] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [projectView, setProjectView] = useState<ProjectView>("Project");
  const [selectedLOBs, setSelectedLOBs] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const wizardContext = useContext(AdvancedExclusionsContext);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // fetch data from api
    setIsLoading(true);
    wizardContext.projectionProviderService.getBulkTargetingProjectsAsync(true, false, undefined, wizardContext.projectType).then((response) => {
      const tabledata: BulkProviderChangeProjectTableData[] = response.map((project) => {
        const detailsArr: string[] = [];
        if (project.isReadOnly) {
          detailsArr.push("Read Only");
        }
        if (project.hasExclusions) {
          detailsArr.push("Existing Excluded Providers");
        }
        if (project.hasTargets) {
          detailsArr.push("Existing Targeted Providers");
        }
        if (!project.hasMarketOrProspect) {
          detailsArr.push("No Market/Prospect Network");
        }
        const detailsStr = detailsArr.join(" And ");

        return {
          /* oxlint-disable sort-keys */
          rowIsDisabled: project.isReadOnly || !project.hasMarketOrProspect,
          isChecked: project.projectId.toString() === wizardContext.currentProjectId,
          key: project.projectId.toString(),
          projectId: project.projectId,
          projectName: project.projectName,
          templateName: project.templateName,
          details: detailsStr,
          tags: project.tags,
          lob: project.lineOfBusiness
          /* oxlint-enable sort-keys */
        };
      });
      wizardContext.setProjects(tabledata);
      setIsLoading(false);
    });
  }, []);

  const renderedProjects = useMemo(() => {
    if (!projectSearchString) {
      return wizardContext.projects.sort((a, b) => a.projectName > b.projectName ? 1 : -1);
    }
    const escapedSearchString = projectSearchString.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
    const regex = new RegExp(escapedSearchString, "i");
    const filteredProjectList = wizardContext.projects.filter((project) => !!project.projectName.match(regex));
    return filteredProjectList.sort((a, b) => a.projectName > b.projectName ? 1 : -1);
  }, [projectSearchString, wizardContext.projects]);

  const handleCheckboxChange = (selectedKeys: string[], isBulk?: boolean) => {
    if (isBulk) {
      let newSelectedProjectIds = [...wizardContext.selectedProjectIds];
      if (selectedKeys.length > 0) {
        newSelectedProjectIds.push(...selectedKeys);
      } else {
        const removedIds = renderedProjects.map((project) => project.projectId.toString());
        newSelectedProjectIds = newSelectedProjectIds.filter((id) => { return !removedIds.includes(id); });
      }
      appendCurrentProjectToList(newSelectedProjectIds);
      wizardContext.setSelectedProjectIds([...new Set(newSelectedProjectIds)]);
    } else {
      appendCurrentProjectToList(selectedKeys);
      wizardContext.setSelectedProjectIds(selectedKeys);
    }
  };

  useEffect(() => {
    props.pageIsValid(wizardContext.selectedProjectIds.length > 0);
  }, [wizardContext.selectedProjectIds]);

  const appendCurrentProjectToList = (projectList: string[]) => {
    const currentProjectExists = wizardContext.currentProjectId != null && wizardContext.currentProjectId !== "";
    if (currentProjectExists && projectList.indexOf(wizardContext.currentProjectId) === -1) {
      // Current project must always be selected
      projectList.push(wizardContext.currentProjectId);
    }
  }

  const projectViewRadioButtons = useMemo(() => {
    return projectViewOptions.map((opt) => {
      const key = opt.toLowerCase().replaceAll(" ", "-");
      return (
        <QESRadioButton
          key={opt}
          label={opt}
          testId={`volume-reports__project-view--${key}`}
          onChange={() => {
            setProjectSearchString("");
            setProjectView(opt)
          }}
          checked={projectView === opt}
          name="project-view"
        />
      );
    });
  }, [projectViewOptions, projectView]);

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter")
      setProjectSearchString(searchRef.current.value);
  };

  const isTagView = useMemo(() => {
    return projectView === "Tag";
  }, [projectView]);

  const isLOBView = useMemo(() => {
    return projectView === "Line of Business";
  }, [projectView]);

  const filterMessage = useMemo(() => {
    if (!projectSearchString || projectSearchString === "") {
      return null;
    }

    return `Currently filtered to ${projectView}s including "${projectSearchString}"`;
  }, [projectSearchString]);

  return (
    <PageSection>
      <div className="container mt-3">
        <p className="copy-text-default">Select the projects you would like to exclude from.</p>
      </div>

      <div className="container mt-3">
        <fieldset className="row">
          <span className="label-s">Search By</span>
          <div className="col-12 d-flex ps-0">
            <QESRadioGroup
              renderInline={true}
              groupContainerClass="col-5 ps-0 d-flex align-items-center"
            >
              {projectViewRadioButtons}
            </QESRadioGroup>
            <QESTextInput
              id="filter-projects"
              testId="volume-reports__search-input"
              defaultValue=""
              qesRef={searchRef}
              label="Search By"
              hideLabel={true}
              onKeyUp={(e) => handleKeyUp(e)}
              containerClassName={`col-6`}
              className="bg-white"
            />
            <div className="col-1 d-inline-flex justify-content-center align-items-center">
              <QESButton
                qesType="secondary"
                onClick={() => setProjectSearchString(searchRef.current.value)}
                qesTestId="volume-reports__search-button"
              >
                Search
              </QESButton>
            </div>
          </div>
        </fieldset>
        <p>{filterMessage || <span dangerouslySetInnerHTML={{ __html: "&nbsp;" }} />}</p>
      </div>

      <div className="container">
        <div className="row">
          <div className="col-12">
            {!isTagView ? (
              !isLOBView ?
                (
                  <Waffles<BulkProviderChangeProjectTableData>
                  columns={ProjectsTableColumnConfig}
                  data={renderedProjects}
                  fullWidth={true}
                  loading={isLoading}
                  handleCheckboxChange={handleCheckboxChange}
                  selectedKeys={wizardContext.selectedProjectIds}
                  showCheckboxes={true}
                  testId="import-targeting-projects-table"
                  totalNumberOfRecords={wizardContext.projects.length}
                  />
                ) :
                (
                  <BulkProjectSelectLOBTable
                    appendCurrentProjectToList={appendCurrentProjectToList}
                    searchString={projectSearchString}
                    selections={selectedLOBs}
                    setSelections={setSelectedLOBs}
                  />
                )
              ) :
              (
                <BulkProjectSelectTagsTable
                  appendCurrentProjectToList={appendCurrentProjectToList}
                  searchString={projectSearchString}
                  selections={selectedTags}
                  setSelections={setSelectedTags}
                  />
              )
            }
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default BulkProjectionSelectProjects;